import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { chatsApi, ChatType } from '../../api/chats';
import { UserContext } from '../../context/UserContext';
import {
    subscribeToChatUpdates,
    disconnectWebSocket,
    initWebSocket,
} from '../../api/websocket';
import { getAccessToken } from '../../api/token';
import HrnbAlert from '../controls/HrnbAlert';
import { SkeletonList } from '../controls/HrnbSkeleton';
import HrnbButton from '../controls/HrnbButton';
import HrnbSearch from '../controls/HrnbSearch';
import ChatListItem from './ChatListItem';
import MessageSearchResults from './MessageSearchResults';
import NewChatModal from './NewChatModal';
import { fetchChatListData } from './chatDataCache';
import { applyPinnedState, toggleChatPin } from '../../utils/chatPins';
import { applyArchivedState, toggleChatArchiveLocal, useArchivedChatIds } from '../../utils/chatArchive';
import { PlusIcon } from '../../icons';
import '../../css/chats/ChatsLayout.css';

/**
 * Двухпанельный layout чатов (как в ВК):
 *  - слева список чатов (~25% ширины)
 *  - справа открытый чат через <Outlet />
 *
 * На мобильных устройствах панели переключаются:
 * если выбран чат — показывается только окно чата,
 * иначе — только список.
 */
export default function ChatsLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);

    // Определяем активный чат по URL
    const pathParts = location.pathname.replace(/^\//, '').split('/');
    const activeChatId = pathParts[0] === 'chats' && pathParts[1] ? pathParts[1] : null;

    const [chats, setChats] = useState([]);
    const [filteredChats, setFilteredChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchMode, setSearchMode] = useState('chats'); // 'chats' | 'messages'
    const [showNewChatModal, setShowNewChatModal] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    const [showArchived, setShowArchived] = useState(false);

    // Навигация по списку чатов с клавиатуры
    const [focusedChatIndex, setFocusedChatIndex] = useState(-1);
    const searchContainerRef = useRef(null);
    const listRef = useRef(null);

    // Предзагруженные данные для ChatListItem (профили, баттлы, presence)
    const [chatData, setChatData] = useState({ profiles: new Map(), battles: new Map(), presences: new Map() });

    const chatUpdatesSubscriptionRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const is429Ref = useRef(false);

    // Архивные ID из localStorage (для синхронизации между компонентами)
    const archivedChatIds = useArchivedChatIds();

    // Ref для доступа к актуальному currentUser внутри fetchChats
    // (избегает stale closure в WS-подписке и polling)
    const currentUserRef = useRef(currentUser);
    useEffect(() => {
        currentUserRef.current = currentUser;
    }, [currentUser]);

    // Ref для актуального fetchChats (вызывается из WS-подписки и polling)
    const fetchChatsRef = useRef(null);

    useEffect(() => {
        fetchChats();
    }, [currentUser]);

    // Polling списка чатов — fallback только если WebSocket не работает
    useEffect(() => {
        if (is429Ref.current) return;

        if (wsConnected) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            return;
        }

        pollingIntervalRef.current = setInterval(() => {
            fetchChatsRef.current?.(true);
        }, 15000);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !wsConnected && !is429Ref.current) {
                fetchChatsRef.current?.(true);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [wsConnected]);

    useEffect(() => {
        filterChats();
    }, [searchQuery, chats, chatData, searchMode]);

    // При очистке поиска переключаемся обратно в режим чатов,
    // чтобы отобразить полный список (если были активны "Сообщения")
    useEffect(() => {
        if (!searchQuery.trim() && searchMode === 'messages') {
            setSearchMode('chats');
        }
    }, [searchQuery, searchMode]);

    // Подписка на обновления чатов через WebSocket
    useEffect(() => {
        let isMounted = true;

        const connectAndSubscribe = () => {
            initWebSocket(
                (data) => {
                    if (!isMounted) return;

                    if (data.error) {
                        console.error('[ChatsLayout] WebSocket error:', data.message);
                        setWsConnected(false);
                    } else if (data.connected) {
                        setWsConnected(true);
                        // Отписываемся от старой подписки перед созданием новой
                        // (защита от дублирования при reconnect)
                        if (chatUpdatesSubscriptionRef.current) {
                            chatUpdatesSubscriptionRef.current.unsubscribe();
                            chatUpdatesSubscriptionRef.current = null;
                        }
                        chatUpdatesSubscriptionRef.current = subscribeToChatUpdates(() => {
                            if (!isMounted) return;
                            fetchChatsRef.current?.(true);
                        });
                    }
                },
                null,
                getAccessToken()
            );
        };

        connectAndSubscribe();

        return () => {
            isMounted = false;
            setWsConnected(false);
            if (chatUpdatesSubscriptionRef.current) {
                chatUpdatesSubscriptionRef.current.unsubscribe();
                chatUpdatesSubscriptionRef.current = null;
            }
            disconnectWebSocket();
        };
    }, []);

    const sortChats = useCallback((list) => {
        return [...list].sort((a, b) => {
            // Закреплённые чаты — наверх
            const aPinned = a.isPinned ? 1 : 0;
            const bPinned = b.isPinned ? 1 : 0;
            if (aPinned !== bPinned) return bPinned - aPinned;
            // Затем — по последнему сообщению (новые выше)
            const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
            const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
            return bTime - aTime;
        });
    }, []);

    const fetchChats = async (silent = false) => {
        if (is429Ref.current) return;

        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const chatsList = await chatsApi.listChats();
            const withPins = applyPinnedState(chatsList);
            const withArchived = applyArchivedState(withPins);
            const sorted = sortChats(withArchived);
            setChats(sorted);
            setFilteredChats(sorted);

            // Батчевая загрузка профилей, баттлов и presence
            const currentProfileId = currentUserRef.current?.profileId || currentUserRef.current?.profiles?.[0]?.id;
            if (currentProfileId && chatsList.length > 0) {
                const data = await fetchChatListData(chatsList, currentProfileId);
                setChatData(data);
            }
        } catch (err) {
            if (err.response?.status === 429) {
                console.warn('[ChatsLayout] Получен 429 — polling отключён');
                is429Ref.current = true;
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            }
            console.error('[ChatsLayout] Error fetching chats:', err);
            if (!silent) {
                setError(err.message || 'Ошибка при загрузке чатов');
                setChats([]);
                setFilteredChats([]);
            }
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    // Обновляем ref для актуального fetchChats (используется в WS и polling)
    fetchChatsRef.current = fetchChats;

    /**
     * Локальное (оптимистичное) обновление превью чата в списке.
     * Не делает API-запрос — мгновенно обновляет lastMessage, время,
     * пересортировывает список. Используется при отправке/получении сообщений.
     *
     * @param {string} chatId — ID чата
     * @param {Object} message — объект сообщения (из WS или REST-ответа)
     * @param {boolean} [incrementUnread] — увеличивать ли счётчик непрочитанных
     */
    const updateChatLocally = useCallback((chatId, message, incrementUnread = false) => {
        if (!chatId || !message) return;

        const msgTime = message.createdAt || message.sentAt || new Date().toISOString();
        const msgContent = message.isDeleted
            ? '[Сообщение удалено]'
            : (message.content || message.text || '[Вложение]');

        // Имя отправителя для групповых чатов
        const senderName = (typeof message.sender === 'object'
            ? (message.sender?.displayName || message.sender?.name)
            : null) || message.senderDisplayName || null;
        const senderId = message.senderId
            || (typeof message.sender === 'object'
                ? (message.sender?.id || message.sender?.userId || message.sender?.profileId)
                : null);

        const patch = (c) => {
            if (c.id !== chatId) return c;
            return {
                ...c,
                lastMessage: msgContent,
                lastMessageAt: msgTime,
                lastMessagePreview: {
                    content: msgContent,
                    senderId: senderId || null,
                    senderName: senderName,
                    sentAt: msgTime,
                },
                unreadCount: incrementUnread
                    ? (c.unreadCount || 0) + 1
                    : c.unreadCount,
            };
        };

        // Применяем patch мгновенно (обновление превью), сортировку — debounce 500ms
        setChats(prev => prev.map(patch));
        setFilteredChats(prev => prev.map(patch));

        scheduleSortChats();
    }, []);

    // Debounced сортировка списков чатов — раз в 500ms после последнего обновления
    const sortChatsTimerRef = useRef(null);
    const scheduleSortChats = useCallback(() => {
        if (sortChatsTimerRef.current) {
            clearTimeout(sortChatsTimerRef.current);
        }
        sortChatsTimerRef.current = setTimeout(() => {
            sortChatsTimerRef.current = null;
            setChats(prev => sortChats(prev));
            setFilteredChats(prev => sortChats(prev));
        }, 500);
    }, [sortChats]);

    // Очистка таймера при размонтировании
    useEffect(() => {
        return () => {
            if (sortChatsTimerRef.current) {
                clearTimeout(sortChatsTimerRef.current);
            }
        };
    }, []);

    const updateChatLocallyRef = useRef(updateChatLocally);
    updateChatLocallyRef.current = updateChatLocally;

    const filterChats = () => {
        if (searchMode !== 'chats') return; // в режиме поиска сообщений не фильтруем чаты

        // Фильтр по архиву: показываем либо активные, либо архивные
        let baseList = chats.filter(chat => showArchived ? chat.isArchived : !chat.isArchived);

        if (!searchQuery.trim()) {
            setFilteredChats(baseList);
            return;
        }

        const query = searchQuery.toLowerCase();
        const currentProfileId = currentUser?.profileId || currentUser?.profiles?.[0]?.id;
        const filtered = baseList.filter((chat) => {
            // Поиск по заголовку чата
            const title = (chat.title || chat.name || '').toLowerCase();
            if (title.includes(query)) return true;

            // Поиск по последнему сообщению
            const lastMessage = (chat.lastMessage || '').toLowerCase();
            if (lastMessage.includes(query)) return true;

            // Поиск по превью последнего сообщения (V2)
            const previewContent = (chat.lastMessagePreview?.content || '').toLowerCase();
            if (previewContent.includes(query)) return true;

            // Для DIRECT-чатов — поиск по имени собеседника (PublicProfile)
            if (chat.chatType === ChatType.DIRECT) {
                const participants = chat.participantIds || chat.participants || [];
                const otherProfileId = participants.find(pId => pId !== currentProfileId) || participants[0];
                if (otherProfileId) {
                    const profile = chatData.profiles.get(otherProfileId);
                    if (profile?.displayName?.toLowerCase().includes(query)) return true;
                }
            }

            // Для BATTLE_SUPPORT — поиск по названию баттла
            if (chat.chatType === ChatType.BATTLE_SUPPORT && chat.battleId) {
                const battle = chatData.battles.get(chat.battleId);
                if (battle?.title?.toLowerCase().includes(query)) return true;
            }

            return false;
        });
        setFilteredChats(sortChats(filtered));
    };

    // Перефильтрация при смене showArchived
    useEffect(() => {
        filterChats();
    }, [showArchived]);

    const handleChatClick = (chatId) => {
        navigate(`/chats/${chatId}`);
    };

    const handleSearchResultClick = useCallback((chatId, messageId) => {
        navigate(`/chats/${chatId}${messageId ? `?msg=${messageId}` : ''}`);
    }, [navigate]);

    const handleTogglePin = useCallback((chatId) => {
        const nowPinned = toggleChatPin(chatId);

        const updateChat = (list) => list.map(c =>
            c.id === chatId ? { ...c, isPinned: nowPinned } : c
        );
        setChats(prev => sortChats(updateChat(prev)));
        setFilteredChats(prev => sortChats(updateChat(prev)));
    }, [sortChats]);

    const handleToggleArchive = useCallback(async (chatId) => {
        // Сначала обновляем локально (мгновенный отклик)
        const nowArchived = toggleChatArchiveLocal(chatId);

        const updateChat = (list) => list.map(c =>
            c.id === chatId ? { ...c, isArchived: nowArchived } : c
        );
        setChats(prev => sortChats(updateChat(prev)));
        setFilteredChats(prev => sortChats(updateChat(prev)));

        // Пытаемся синхронизировать с сервером
        try {
            await chatsApi.toggleArchive(chatId);
        } catch (err) {
            console.warn('[ChatsLayout] Server toggle-archive failed, using localStorage only:', err.message);
            // localStorage уже обновлён в toggleChatArchiveLocal — этого достаточно
        }
    }, [sortChats]);

    const focusSearch = useCallback(() => {
        const input = searchContainerRef.current?.querySelector('input');
        if (input) {
            input.focus();
            input.select();
        }
    }, []);

    // Сброс фокуса при смене фильтра
    useEffect(() => {
        setFocusedChatIndex(-1);
    }, [filteredChats]);

    // Прокрутка к сфокусированному элементу списка
    useEffect(() => {
        if (focusedChatIndex < 0) return;
        const listEl = listRef.current;
        if (!listEl) return;
        const item = listEl.children[focusedChatIndex];
        if (item) {
            item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [focusedChatIndex]);

    // Глобальные горячие клавиши: Ctrl+K (поиск), ↑/↓ (навигация), Enter (открыть)
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ctrl+K / Cmd+K — фокус на поиск чатов
            if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K' || e.key === 'К' || e.key === 'к')) {
                e.preventDefault();
                e.stopPropagation();
                if (activeChatId) {
                    navigate('/chats');
                    setTimeout(() => focusSearch(), 100);
                } else {
                    focusSearch();
                }
                return;
            }

            // ↑/↓ и Enter — только если фокус не в поле ввода и не в режиме поиска сообщений
            // (в режиме сообщений навигацию обрабатывает MessageSearchResults)
            const tag = e.target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
            if (searchMode === 'messages' && searchQuery.trim()) return;

            if (e.key === 'ArrowDown' && filteredChats.length > 0) {
                e.preventDefault();
                setFocusedChatIndex(prev => {
                    if (prev < 0) return 0;
                    return Math.min(prev + 1, filteredChats.length - 1);
                });
            } else if (e.key === 'ArrowUp' && filteredChats.length > 0) {
                e.preventDefault();
                setFocusedChatIndex(prev => {
                    if (prev < 0) return filteredChats.length - 1;
                    return Math.max(prev - 1, 0);
                });
            } else if (e.key === 'Enter' && focusedChatIndex >= 0 && focusedChatIndex < filteredChats.length) {
                e.preventDefault();
                handleChatClick(filteredChats[focusedChatIndex].id);
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [filteredChats, focusedChatIndex, activeChatId, navigate, focusSearch, searchMode, searchQuery]);

    const handleNewChatCreated = (newChat) => {
        fetchChats();
        setShowNewChatModal(false);
        // Открываем созданный чат
        if (newChat?.id) {
            navigate(`/chats/${newChat.id}`);
        }
    };

    return (
        <div className="chats-layout">
            {/* Левая панель — список чатов */}
            <aside
                className={`chats-layout__sidebar${
                    activeChatId ? ' chats-layout__sidebar--hidden-mobile' : ''
                }`}
            >
                <div className="chats-layout__sidebar-header">
                    <div className="chats-layout__sidebar-title-row">
                        <h2 className="chats-layout__sidebar-title">
                            {showArchived ? 'Архив' : 'Чаты'}
                        </h2>
                        <kbd className="chats-layout__kbd-hint" title="Быстрый поиск (Ctrl+K)">Ctrl+K</kbd>
                        <HrnbButton
                            variant="light"
                            size="sm"
                            onClick={() => setShowArchived(prev => !prev)}
                            className="chats-layout__archive-toggle-btn"
                            title={showArchived ? 'Показать активные чаты' : 'Показать архив'}
                        >
                            {showArchived ? '📋' : '📦'}
                        </HrnbButton>
                        <HrnbButton
                            onClick={() => setShowNewChatModal(true)}
                            className="chats-layout__new-chat-btn"
                            icon={<PlusIcon />}
                        >
                            Новый
                        </HrnbButton>
                    </div>
                    <div className="chats-layout__search" ref={searchContainerRef}>
                        <HrnbSearch
                            placeholder="Поиск чатов..."
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                        {searchQuery.trim() && (
                            <div className="chats-layout__search-mode">
                                <button
                                    className={`chats-layout__search-mode-btn ${searchMode === 'chats' ? 'chats-layout__search-mode-btn--active' : ''}`}
                                    onClick={() => setSearchMode('chats')}
                                >
                                    Чаты
                                </button>
                                <button
                                    className={`chats-layout__search-mode-btn ${searchMode === 'messages' ? 'chats-layout__search-mode-btn--active' : ''}`}
                                    onClick={() => setSearchMode('messages')}
                                >
                                    Сообщения
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="chats-layout__sidebar-content">
                    {searchMode === 'messages' && searchQuery.trim() ? (
                        <MessageSearchResults
                            query={searchQuery}
                            chats={chats}
                            chatData={chatData}
                            currentUser={currentUser}
                            onNavigate={handleSearchResultClick}
                        />
                    ) : (
                        <>
                            {loading && <SkeletonList count={6} />}

                            {error && (
                                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                                    {error}
                                </HrnbAlert>
                            )}

                            {!loading && !error && filteredChats.length === 0 && (
                                <div className="chats-layout__empty">
                                    {showArchived
                                        ? 'Архив пуст'
                                        : searchQuery
                                            ? 'Чаты не найдены'
                                            : 'У вас пока нет чатов. Создайте новый!'}
                                </div>
                            )}

                            {!loading && !error && filteredChats.length > 0 && (
                                <div className="chats-layout__list" ref={listRef}>
                                    {filteredChats.map((chat, index) => {
                                        const participants = chat.participantIds || chat.participants || [];
                                        const otherProfileId = chat.chatType === 'DIRECT'
                                            ? (participants.find(pId => pId !== (currentUser?.profileId || currentUser?.profiles?.[0]?.id)) || participants[0])
                                            : null;
                                        return (
                                            <ChatListItem
                                                key={chat.id}
                                                chat={chat}
                                                currentUser={currentUser}
                                                onClick={() => handleChatClick(chat.id)}
                                                onTogglePin={() => handleTogglePin(chat.id)}
                                                onArchive={() => handleToggleArchive(chat.id)}
                                                isActive={chat.id === activeChatId}
                                                isFocused={index === focusedChatIndex}
                                                profileData={otherProfileId ? chatData.profiles.get(otherProfileId) : null}
                                                battleData={chat.battleId ? chatData.battles.get(chat.battleId) : null}
                                                presenceData={chatData.presences}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </aside>

            {/* Правая панель — открытый чат или заглушка */}
            <main
                className={`chats-layout__main${
                    !activeChatId ? ' chats-layout__main--hidden-mobile' : ''
                }`}
            >
                <Outlet context={{
                    wsConnected,
                    refreshChats: () => fetchChatsRef.current?.(true),
                    updateChatLocally: (chatId, message, incrementUnread) =>
                        updateChatLocallyRef.current?.(chatId, message, incrementUnread),
                }} />
            </main>

            {showNewChatModal && (
                <NewChatModal
                    onClose={() => setShowNewChatModal(false)}
                    onChatCreated={handleNewChatCreated}
                />
            )}
        </div>
    );
}
